import React, { useCallback, useEffect, useState } from "react";
import * as Mui from "@mui/material";
import {
  AddOutlined,
  CategoryOutlined,
  CurrencyRupeeOutlined,
  EditOutlined,
  EventOutlined,
  InfoOutlined,
  ReceiptLongOutlined,
  ReceiptOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { designColors, designShell, designType } from "services/constants/designTokens";
import { GlobalConfirmDialog, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { tooltipStyleProps } from "services/constants/Styles";
import { ViewActionFooter } from "pages/PayRoll/PayInputs/Shared/ViewActionFooter";
import { showToast } from "services/utils/Status";
import { ConfigTableCard } from "pages/PayRoll/Shared/ConfigurationUi";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import {
  API_FIELD_TO_FORM,
  EMPTY_REIMBURSEMENT_TYPE,
  toForm,
  fromForm,
  validateReimbursementType,
  formatLimit,
} from "./reimbursementTypesData";
import {
  createReimbursementType,
  deleteReimbursementType,
  fetchPayslipLines,
  fetchReimbursementTypes,
  updateReimbursementType,
} from "pages/PayRoll/Shared/reimbursementApi";

const headSx = { ...designShell.tableHeadCellBase, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, ...designType.tableBodyCell };
const numberSx = { textAlign: "right" };

const COLUMNS = [
  { key: "name", label: "Reimbursement Type" },
  { key: "limitPerClaim", label: "Limit per Claim (₹)", numeric: true },
  { key: "limitPerMonth", label: "Limit per Month (₹)", numeric: true },
  { key: "billWithinDays", label: "Bill Within (days)", numeric: true },
  { key: "billNeeded", label: "Bill Needed" },
  { key: "payslipLine", label: "Payslip Line" },
  { key: "status", label: "Status" },
];

const tealSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: designColors.tealDark },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: designColors.teal },
};

const helperSx = { ...designType.captionText, mt: 1 };

// Add / Edit Reimbursement Type side panel. `editing` is the row being
// edited, or null when adding. Cancel goes back to the view panel when
// editing (onCancel), or closes when adding.
const ReimbursementTypePanel = ({ open, editing, rows, payslipLines, payslipError, saving, serverErrors, onClose, onCancel, onSave }) => {
  const [form, setForm] = useState(() => (editing ? toForm(editing) : toForm(EMPTY_REIMBURSEMENT_TYPE)));
  const [submitted, setSubmitted] = useState(false);
  const errors = validateReimbursementType(form, rows, editing?.id);
  // Server field errors show until that field is changed.
  const [touched, setTouched] = useState(() => new Set());
  useEffect(() => setTouched(new Set()), [serverErrors]);
  const shownErrors = {
    ...Object.fromEntries(Object.entries(serverErrors || {}).filter(([key]) => !touched.has(key))),
    ...(submitted ? errors : {}),
  };
  const set = (key) => (value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => new Set(prev).add(key));
  };

  // The type's current payslip line stays selectable even if that pay
  // component is no longer active (the list only has active ones).
  const payslipOptions = editing?.payComponentId && !payslipLines.some((line) => line.value === editing.payComponentId)
    ? [...payslipLines, { value: editing.payComponentId, label: editing.payslipLine || `Pay component ${editing.payComponentId}` }]
    : payslipLines;

  const handleSave = () => {
    setSubmitted(true);
    if (Object.keys(errors).length || saving) return;
    onSave(fromForm(form));
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={editing ? "Edit Reimbursement Type" : "Add Reimbursement Type"}
      subtitle={editing ? editing.name : "Employees can choose this type when they claim."}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={() => { if (!saving) onCancel(); }}
          primaryLabel={saving ? "Saving..." : editing ? "Save" : "Add type"}
          primaryProps={{ onClick: handleSave, disabled: saving }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        <FormSection title="Type">
          <Field label="Name *" helper="Shown to employees in the claim drop-down. Must be unique." error={shownErrors.name} wide>
            <TextField value={form.name} onChange={(value) => set("name")(value.slice(0, 100))} placeholder="e.g. Travel / Conveyance" error={shownErrors.name} />
          </Field>
          <Field label="Help text for employees" helper="Shown under the drop-down when this type is picked (optional)." error={shownErrors.helpText} wide>
            <TextField type="multiline" value={form.helpText} onChange={(value) => set("helpText")(value.slice(0, 255))} placeholder="What this type covers, shown to employees when they claim" />
          </Field>
        </FormSection>

        <FormSection
          title="Limits"
          footer={<Mui.Typography sx={helperSx}>Claims over a limit cannot be submitted.</Mui.Typography>}
        >
          <Field label="Limit per claim (₹)" helper="Max per claim. Blank = no limit." error={shownErrors.limitPerClaim}>
            <TextField type="number" value={form.limitPerClaim} onChange={set("limitPerClaim")} placeholder="Leave blank for no limit" error={shownErrors.limitPerClaim} />
          </Field>
          <Field label="Limit per month (₹)" helper="Max per month. Blank = no limit." error={shownErrors.limitPerMonth}>
            <TextField type="number" value={form.limitPerMonth} onChange={set("limitPerMonth")} placeholder="Leave blank for no limit" error={shownErrors.limitPerMonth} />
          </Field>
          <Field label="Bill must be within (days) *" helper="Oldest bill date allowed, counted back from the claim date." error={shownErrors.billWithinDays} wide>
            <TextField type="number" value={form.billWithinDays} onChange={set("billWithinDays")} placeholder="e.g. 60" error={shownErrors.billWithinDays} />
          </Field>
        </FormSection>

        <FormSection title="Bill">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <Mui.FormControlLabel
              control={
                <Mui.Checkbox
                  checked={form.billNeeded}
                  onChange={(event) => set("billNeeded")(event.target.checked)}
                  sx={{ p: 0, mr: 1, color: designColors.slate400, "&.Mui-checked": { color: designColors.teal } }}
                />
              }
              label="Bill number or photo needed"
              sx={{ m: 0, "& .MuiFormControlLabel-label": designType.tableBodyCell }}
            />
            <Mui.Typography sx={helperSx}>
              When bill upload is Off, employees give the paper bill to HR; the bill number is optional.
            </Mui.Typography>
          </Mui.Box>
        </FormSection>

        <FormSection
          title="Payslip"
          footer={
            <Mui.Typography sx={helperSx}>
              The pay component decides the payslip label. Whether it counts for PF / ESI / PT / LWF is set in Statutory configuration.
            </Mui.Typography>
          }
        >
          <Field label="Payslip line (pay component) *" helper="Salary component the amount is paid under." error={shownErrors.payComponentId || payslipError} wide>
            <SelectField
              value={form.payComponentId}
              onChange={(value) => set("payComponentId")(value === "" ? "" : Number(value))}
              options={payslipOptions}
              placeholder={payslipOptions.length ? "Select pay component" : "No Reimbursement pay components found"}
              error={shownErrors.payComponentId || payslipError}
            />
          </Field>
        </FormSection>

        <FormSection title="Status">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              <Mui.Switch
                checked={form.active}
                onChange={(event) => set("active")(event.target.checked)}
                inputProps={{ "aria-label": "Active" }}
                sx={tealSwitchSx}
              />
              <Mui.Typography sx={designType.tableBodyCell}>
                {form.active ? "Active — shown in the employee's drop-down" : "Inactive — hidden from the employee's drop-down"}
              </Mui.Typography>
            </Mui.Stack>
            <Mui.Typography sx={helperSx}>
              Changes apply to new claims only. Claims already submitted keep the rules they were made under.
            </Mui.Typography>
          </Mui.Box>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// View Reimbursement Type: read-only details, Edit icon in the header (opens
// the edit form) and Delete as a full-width footer button, like the Pay
// Inputs view panels. A type used in a claim can't be deleted (made
// Inactive instead), so Delete is disabled with the reason as a tooltip.
const ReimbursementTypeViewPanel = ({ open, row, busy, onClose, onEdit, onDelete }) => {
  if (!row) return null;
  const { indigo, blue, green, orange, purple, pink } = detailAccentPalette;
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Reimbursement Type"
      subtitle={row.name}
      bodySx={panelBodySx}
      headerActions={
        <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
          <span>
            <Mui.IconButton
              size="small"
              aria-label="Edit"
              disabled={busy}
              onClick={onEdit}
              sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
            >
              <EditOutlined fontSize="small" />
            </Mui.IconButton>
          </span>
        </Mui.Tooltip>
      }
      footer={
        <ViewActionFooter
          recordAction={{
            label: "Delete Reimbursement Type",
            onClick: onDelete,
            disabled: busy || row.used,
            tooltip: row.used ? "Used in claims, so it can't be deleted. Make it Inactive instead." : undefined,
          }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        <RequestDetailsSection
          title="TYPE"
          fields={[
            { icon: CategoryOutlined, label: "Name", value: row.name || "–", accent: indigo },
            { icon: TaskAltOutlined, label: "Status", value: row.active ? "Active" : "Inactive", accent: green },
            { icon: InfoOutlined, label: "Help text for employees", value: row.helpText || "–", accent: blue },
          ]}
        />
        <RequestDetailsSection
          title="LIMITS"
          fields={[
            { icon: CurrencyRupeeOutlined, label: "Limit per claim (₹)", value: row.limitPerClaim == null ? "No limit" : formatLimit(row.limitPerClaim), accent: orange },
            { icon: CurrencyRupeeOutlined, label: "Limit per month (₹)", value: row.limitPerMonth == null ? "No limit" : formatLimit(row.limitPerMonth), accent: orange },
            { icon: EventOutlined, label: "Bill must be within", value: row.billWithinDays == null ? "–" : `${row.billWithinDays} days`, accent: purple },
          ]}
        />
        <RequestDetailsSection
          title="BILL & PAYSLIP"
          fields={[
            { icon: ReceiptOutlined, label: "Bill number or photo needed", value: row.billNeeded ? "Yes" : "No", accent: pink },
            { icon: ReceiptLongOutlined, label: "Payslip line", value: row.payslipLine || "–", accent: indigo },
          ]}
        />
        {row.hint ? (
          <Mui.Typography sx={{ ...designType.captionText, px: 0.5 }}>
            Employees see: {row.hint}
          </Mui.Typography>
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const renderCell = (row, column) => {
  switch (column.key) {
    case "limitPerClaim":
    case "limitPerMonth":
      return formatLimit(row[column.key]);
    case "billWithinDays":
      return row.billWithinDays;
    case "billNeeded":
      return row.billNeeded ? "Yes" : "No";
    case "status":
      return <ToneChip label={row.active ? "Active" : "Inactive"} tone={row.active ? "green" : "slate"} />;
    default:
      return row[column.key];
  }
};

// Reimbursement Types card on Payroll > Configuration > Pay Input >
// Reimbursements, on the reimbursement types/ API. Replaces the old Payroll >
// Configuration > Expense Type screen. The server adds the 10 starter types
// the first time a company opens this page.
export const ReimbursementTypesCard = ({ domain, companyId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [payslipLines, setPayslipLines] = useState([]);
  const [payslipError, setPayslipError] = useState("");
  // { mode: "view" | "edit" | "add", id } - the row is looked up by id so
  // the view shows the saved values after an edit.
  const [panel, setPanel] = useState(null);
  const panelRow = panel?.id != null ? rows.find((row) => row.id === panel.id) || null : null;
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    fetchReimbursementTypes({ domain, companyId, signal: controller.signal })
      .then((types) => { if (!controller.signal.aborted) setRows(types); })
      .catch((error) => { if (!controller.signal.aborted) setLoadError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [domain, companyId, reload]);

  useEffect(() => {
    const controller = new AbortController();
    setPayslipError("");
    fetchPayslipLines({ domain, companyId, signal: controller.signal })
      .then((lines) => { if (!controller.signal.aborted) setPayslipLines(lines); })
      .catch((error) => { if (!controller.signal.aborted) setPayslipError(`Couldn't load payslip lines: ${error.message}`); });
    return () => controller.abort();
  }, [domain, companyId]);

  const closePanel = useCallback(() => {
    if (saving) return;
    setPanel(null);
    setServerErrors({});
  }, [saving]);

  const handleSave = async (values) => {
    setSaving(true);
    setServerErrors({});
    try {
      const editingId = panel?.mode === "edit" ? panel.id : null;
      const result = editingId
        ? await updateReimbursementType({ domain, id: editingId, values })
        : await createReimbursementType({ domain, companyId, values });
      setRows((prev) => (editingId
        ? prev.map((row) => (row.id === editingId ? result.type : row))
        : [...prev, result.type]));
      showToast("success", result.message || (editingId ? "Reimbursement type saved." : "Reimbursement type added."));
      // Back to the view of the saved type.
      setPanel({ mode: "view", id: result.type.id ?? editingId });
    } catch (error) {
      setServerErrors(Object.fromEntries(
        Object.entries(error.fieldErrors || {}).map(([field, message]) => [API_FIELD_TO_FORM[field] || field, message]),
      ));
      showToast("error", error.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const target = deleting;
    setSaving(true);
    try {
      await deleteReimbursementType({ domain, id: target.id });
      setRows((prev) => prev.filter((row) => row.id !== target.id));
      setPanel(null);
      showToast("success", "Reimbursement type deleted.");
    } catch (error) {
      showToast("error", error.message);
      // TYPE_IN_USE: a claim started using it; reload so Delete hides.
      if (error.code === "TYPE_IN_USE") setReload((value) => value + 1);
    } finally {
      setDeleting(null);
      setSaving(false);
    }
  };

  return (
    <ConfigTableCard
      icon={CurrencyRupeeOutlined}
      tint="amber"
      title="Reimbursement Types"
      description="The types employees can choose when they claim, with limits and the payslip line. Only Active types appear in the drop-down."
      action={
        <PrimaryButton startIcon={<AddOutlined />} disabled={loading || Boolean(loadError)} onClick={() => { setServerErrors({}); setPanel({ mode: "add", id: null }); }}>
          Add type
        </PrimaryButton>
      }
      note="Starter list from Cauver — rename, change or delete any. A type already used in a claim can be made Inactive but not deleted."
    >
      <Mui.TableContainer sx={{ overflowX: "auto" }}>
        <Mui.Table size="small" sx={{ minWidth: 880 }}>
          <Mui.TableHead>
            <Mui.TableRow>
              {COLUMNS.map((column) => (
                <Mui.TableCell key={column.key} sx={{ ...headSx, ...(column.numeric ? numberSx : {}) }}>
                  {column.label}
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {loading ? (
              <Mui.TableRow>
                <Mui.TableCell colSpan={COLUMNS.length} sx={{ ...cellSx, textAlign: "center", py: 4 }}>
                  <Mui.CircularProgress size={22} sx={{ color: designColors.teal }} aria-label="Loading reimbursement types" />
                </Mui.TableCell>
              </Mui.TableRow>
            ) : loadError ? (
              <Mui.TableRow>
                <Mui.TableCell colSpan={COLUMNS.length} sx={{ ...cellSx, py: 2 }}>
                  <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setReload((value) => value + 1)}>Retry</Mui.Button>}>
                    {loadError}
                  </Mui.Alert>
                </Mui.TableCell>
              </Mui.TableRow>
            ) : rows.length === 0 ? (
              <Mui.TableRow>
                <Mui.TableCell colSpan={COLUMNS.length} sx={{ ...cellSx, textAlign: "center", py: 4, color: designColors.slate600 }}>
                  No reimbursement types yet. Add one to let employees claim.
                </Mui.TableCell>
              </Mui.TableRow>
            ) : (
              rows.map((row) => (
                <Mui.TableRow
                  key={row.id}
                  hover
                  tabIndex={0}
                  aria-label={`View ${row.name}`}
                  onClick={() => setPanel({ mode: "view", id: row.id })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setPanel({ mode: "view", id: row.id });
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    ...(panel?.id === row.id ? designShell.neutralSelectedRow : {}),
                  }}
                >
                  {COLUMNS.map((column) => (
                    <Mui.TableCell key={column.key} sx={{ ...cellSx, ...(column.numeric ? numberSx : {}) }}>
                      {renderCell(row, column)}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>

      {panel?.mode === "view" && panelRow ? (
        <ReimbursementTypeViewPanel
          open
          row={panelRow}
          busy={saving}
          onClose={closePanel}
          onEdit={() => { setServerErrors({}); setPanel({ mode: "edit", id: panelRow.id }); }}
          onDelete={() => setDeleting(panelRow)}
        />
      ) : null}
      {panel?.mode === "add" || (panel?.mode === "edit" && panelRow) ? (
        <ReimbursementTypePanel
          key={panel.mode === "edit" ? panelRow.id : "new"}
          open
          editing={panel.mode === "edit" ? panelRow : null}
          rows={rows}
          payslipLines={payslipLines}
          payslipError={payslipError}
          saving={saving}
          serverErrors={serverErrors}
          onClose={closePanel}
          onCancel={() => {
            setServerErrors({});
            if (panel.mode === "edit") setPanel({ mode: "view", id: panelRow.id });
            else setPanel(null);
          }}
          onSave={handleSave}
        />
      ) : null}

      <GlobalConfirmDialog
        open={Boolean(deleting)}
        title="Delete reimbursement type?"
        confirmLabel="Delete"
        cancelLabel="Keep"
        onCancel={() => { if (!saving) setDeleting(null); }}
        onConfirm={confirmDelete}
      >
        {deleting ? `"${deleting.name}" will be removed from the list. This cannot be undone.` : null}
      </GlobalConfirmDialog>
    </ConfigTableCard>
  );
};
